import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Plus, DollarSign, BarChart3, Target, Play, Pause, 
  TrendingUp, Eye, MousePointer, Users, Calendar, CreditCard,
  Building2, Globe, ChevronRight, AlertCircle, CheckCircle2
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdvertiserDashboard = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [advertiser, setAdvertiser] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showCreateCampaignModal, setShowCreateCampaignModal] = useState(false);
  const [showCreateAdModal, setShowCreateAdModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    fetchAdvertiserProfile();
  }, []);

  const fetchAdvertiserProfile = async () => {
    try {
      const response = await axios.get(`${API}/advertiser/me`);
      setAdvertiser(response.data);
      fetchCampaigns();
    } catch (error) {
      if (error.response?.status === 404) {
        setShowRegisterModal(true);
      }
      console.error('Error fetching advertiser profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/advertiser/campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };

  const fetchCampaignAnalytics = async (campaignId) => {
    try {
      const response = await axios.get(`${API}/advertiser/campaigns/${campaignId}/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const handleAddFunds = async (amount) => {
    try {
      const response = await axios.post(`${API}/advertiser/billing/add-funds`, null, {
        params: { amount }
      });
      if (response.data.checkout_url) {
        window.open(response.data.checkout_url, '_blank');
      }
    } catch (error) {
      console.error('Error adding funds:', error);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'campaigns', label: 'Campaigns', icon: Target },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp },
    { id: 'billing', label: 'Billing', icon: CreditCard }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-[#08080C] rounded-3xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col border border-white/10"
        data-testid="advertiser-dashboard"
      >
        {/* Header */}
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-[#FFE800] to-[#FF2A2A] rounded-2xl flex items-center justify-center shadow-lg shadow-[#FFE800]/20">
              <Target className="w-6 h-6 text-black" />
            </div>
            <div>
              <h2 className="text-2xl font-black tracking-tight text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Advertiser Hub
              </h2>
              <p className="text-sm text-gray-500">
                {advertiser ? advertiser.company_name : 'Reach millions of engaged users'}
              </p>
            </div>
          </div>
          <button
            data-testid="close-advertiser-dashboard"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#FFE800] border-t-transparent" />
          </div>
        ) : !advertiser ? (
          <RegisterAdvertiserView onRegister={fetchAdvertiserProfile} />
        ) : (
          <div className="flex-1 flex overflow-hidden">
            {/* Sidebar Navigation */}
            <div className="w-56 border-r border-white/10 p-4 space-y-2 bg-[#050507]">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  data-testid={`tab-${tab.id}`}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    activeTab === tab.id
                      ? 'bg-[#FFE800]/10 border border-[#FFE800]/20 text-[#FFE800]'
                      : 'hover:bg-white/5 text-gray-400 hover:text-white'
                  }`}
                >
                  <tab.icon className="w-5 h-5" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              ))}

              {/* Quick Stats */}
              <div className="mt-6 pt-6 border-t border-white/10">
                <div className="px-4 py-3 rounded-xl bg-[#FFE800]/10 border border-[#FFE800]/20">
                  <p className="text-xs font-medium mb-1 text-gray-500">Balance</p>
                  <p className="text-xl font-bold text-[#FFE800]">${advertiser.balance?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 overflow-y-auto p-6 bg-[#030305]">
              {activeTab === 'overview' && (
                <OverviewTab 
                  advertiser={advertiser} 
                  campaigns={campaigns}
                  onCreateCampaign={() => setShowCreateCampaignModal(true)}
                />
              )}
              {activeTab === 'campaigns' && (
                <CampaignsTab 
                  campaigns={campaigns}
                  onCreateCampaign={() => setShowCreateCampaignModal(true)}
                  onSelectCampaign={(campaign) => {
                    setSelectedCampaign(campaign);
                    fetchCampaignAnalytics(campaign.id);
                  }}
                  onCreateAd={(campaign) => {
                    setSelectedCampaign(campaign);
                    setShowCreateAdModal(true);
                  }}
                />
              )}
              {activeTab === 'analytics' && (
                <AnalyticsTab 
                  analytics={analytics}
                  campaigns={campaigns}
                  onSelectCampaign={(campaign) => fetchCampaignAnalytics(campaign.id)}
                />
              )}
              {activeTab === 'billing' && (
                <BillingTab 
                  advertiser={advertiser}
                  onAddFunds={handleAddFunds}
                />
              )}
            </div>
          </div>
        )}
      </motion.div>

      {/* Create Campaign Modal */}
      <AnimatePresence>
        {showCreateCampaignModal && (
          <CreateCampaignModal
            onClose={() => setShowCreateCampaignModal(false)}
            onCreated={() => {
              fetchCampaigns();
              setShowCreateCampaignModal(false);
            }}
          />
        )}
      </AnimatePresence>

      {/* Create Ad Modal */}
      <AnimatePresence>
        {showCreateAdModal && selectedCampaign && (
          <CreateAdModal
            campaign={selectedCampaign}
            onClose={() => {
              setShowCreateAdModal(false);
              setSelectedCampaign(null);
            }}
            onCreated={() => {
              fetchCampaigns();
              setShowCreateAdModal(false);
              setSelectedCampaign(null);
            }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Register Advertiser View
const RegisterAdvertiserView = ({ onRegister }) => {
  const [formData, setFormData] = useState({
    company_name: '',
    website: '',
    industry: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const industries = [
    'Technology', 'E-commerce', 'Finance', 'Healthcare', 'Education',
    'Entertainment', 'Travel', 'Food & Beverage', 'Fashion', 'Other'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/advertiser/register`, null, {
        params: formData
      });
      setSuccess(true);
      setTimeout(() => onRegister(), 2000);
    } catch (error) {
      console.error('Error registering advertiser:', error);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center">
          <div className="w-20 h-20 bg-[#00FF66]/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-[#00FF66]/20">
            <CheckCircle2 className="w-10 h-10 text-[#00FF66]" />
          </div>
          <h3 className="text-2xl font-bold mb-2 text-white">Application Submitted!</h3>
          <p className="text-gray-500">We'll review your application and get back to you within 24 hours.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-[#FFE800] to-[#FF2A2A] rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-[#FFE800]/20">
            <Building2 className="w-10 h-10 text-black" />
          </div>
          <h3 className="text-2xl font-bold mb-2 text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Become an Advertiser
          </h3>
          <p className="text-gray-500">
            Reach millions of engaged users on Pulsate Global with targeted ads
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-300">Company Name</label>
            <input
              data-testid="company-name-input"
              type="text"
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              placeholder="Your company name"
              className="w-full px-4 py-3 rounded-xl border bg-[#0F0F16] border-white/10 text-white placeholder-gray-600 outline-none focus:border-[#FFE800] focus:ring-2 focus:ring-[#FFE800]/20 transition-all"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-gray-300">Website</label>
            <input
              data-testid="website-input"
              type="url"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              placeholder="https://yourcompany.com"
              className="w-full px-4 py-3 rounded-xl border bg-[#0F0F16] border-white/10 text-white placeholder-gray-600 outline-none focus:border-[#FFE800] focus:ring-2 focus:ring-[#FFE800]/20 transition-all"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-gray-300">Industry</label>
            <select
              data-testid="industry-select"
              value={formData.industry}
              onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border bg-[#0F0F16] border-white/10 text-white outline-none focus:border-[#FFE800] focus:ring-2 focus:ring-[#FFE800]/20 transition-all"
              required
            >
              <option value="" className="bg-[#0F0F16]">Select industry</option>
              {industries.map((ind) => (
                <option key={ind} value={ind} className="bg-[#0F0F16]">{ind}</option>
              ))}
            </select>
          </div>

          <motion.button
            data-testid="register-advertiser-button"
            type="submit"
            disabled={loading}
            whileHover={{ scale: 1.02, boxShadow: '0 0 20px rgba(255,232,0,0.3)' }}
            whileTap={{ scale: 0.98 }}
            className="w-full py-4 rounded-xl font-semibold text-black bg-[#FFE800] shadow-lg shadow-[#FFE800]/20 disabled:opacity-50 transition-all"
          >
            {loading ? 'Submitting...' : 'Apply to Advertise'}
          </motion.button>
        </form>
      </div>
    </div>
  );
};

// Overview Tab
const OverviewTab = ({ advertiser, campaigns, onCreateCampaign }) => {
  const stats = advertiser.stats || {};
  
  return (
    <div className="space-y-6">
      {/* Status Banner */}
      {advertiser.status === 'pending' && (
        <div className="p-4 rounded-2xl flex items-center gap-3 bg-[#FFE800]/10 border border-[#FFE800]/20">
          <AlertCircle className="w-5 h-5 text-[#FFE800]" />
          <p className="text-sm font-medium text-[#FFE800]">
            Your advertiser account is pending approval. We'll notify you once approved.
          </p>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        {[
          { label: 'Active Campaigns', value: stats.active_campaigns || 0, icon: Target, color: '#FFE800' },
          { label: 'Total Impressions', value: (stats.total_impressions || 0).toLocaleString(), icon: Eye, color: '#00F0FF' },
          { label: 'Total Clicks', value: (stats.total_clicks || 0).toLocaleString(), icon: MousePointer, color: '#00FF66' },
          { label: 'CTR', value: `${(stats.ctr || 0).toFixed(2)}%`, icon: TrendingUp, color: '#FF2A2A' }
        ].map((stat, index) => (
          <div key={index} className="p-4 sm:p-5 rounded-2xl bg-[#08080C] border border-white/10">
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <stat.icon className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: stat.color }} />
            </div>
            <p className="text-xl sm:text-2xl font-bold mb-1 text-white">{stat.value}</p>
            <p className="text-xs sm:text-sm text-gray-500">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <button
          data-testid="create-campaign-button"
          onClick={onCreateCampaign}
          disabled={advertiser.status !== 'approved'}
          className="p-6 rounded-2xl border-2 border-dashed border-white/20 flex items-center gap-4 hover:bg-[#FFE800]/5 hover:border-[#FFE800]/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="w-12 h-12 rounded-xl bg-[#FFE800]/10 flex items-center justify-center border border-[#FFE800]/20">
            <Plus className="w-6 h-6 text-[#FFE800]" />
          </div>
          <div className="text-left">
            <p className="font-semibold text-white">Create Campaign</p>
            <p className="text-sm text-gray-500">Launch a new ad campaign</p>
          </div>
        </button>

        <div className="p-6 rounded-2xl bg-gradient-to-br from-[#FFE800]/20 to-[#FF2A2A]/20 border border-[#FFE800]/20">
          <p className="text-gray-400 text-sm mb-2">Total Spent</p>
          <p className="text-3xl font-bold text-white mb-1">${(advertiser.total_spent || 0).toFixed(2)}</p>
          <p className="text-gray-500 text-sm">{stats.total_campaigns || 0} campaigns total</p>
        </div>
      </div>

      {/* Recent Campaigns */}
      {campaigns.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4 text-white">Recent Campaigns</h3>
          <div className="space-y-3">
            {campaigns.slice(0, 3).map((campaign) => (
              <CampaignCard key={campaign.id} campaign={campaign} compact />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Campaigns Tab
const CampaignsTab = ({ campaigns, onCreateCampaign, onSelectCampaign, onCreateAd }) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white">Your Campaigns</h3>
        <motion.button
          data-testid="new-campaign-button"
          onClick={onCreateCampaign}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-black bg-[#FFE800] shadow-lg shadow-[#FFE800]/20"
        >
          <Plus className="w-4 h-4" />
          New Campaign
        </motion.button>
      </div>

      {campaigns.length === 0 ? (
        <div className="text-center py-12">
          <Target className="w-12 h-12 mx-auto mb-4 text-gray-600" />
          <p className="text-lg font-medium mb-2 text-white">No campaigns yet</p>
          <p className="text-sm mb-4 text-gray-500">Create your first campaign to start advertising</p>
          <button
            onClick={onCreateCampaign}
            className="px-6 py-3 rounded-xl font-medium text-black bg-[#FFE800]"
          >
            Create Campaign
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {campaigns.map((campaign) => (
            <CampaignCard 
              key={campaign.id} 
              campaign={campaign}
              onSelect={() => onSelectCampaign(campaign)}
              onCreateAd={() => onCreateAd(campaign)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Campaign Card
const CampaignCard = ({ campaign, compact, onSelect, onCreateAd }) => {
  const statusColors = {
    pending: { bg: '#FFE800', bgAlpha: '10', border: '20' },
    active: { bg: '#00FF66', bgAlpha: '10', border: '20' },
    paused: { bg: '#6B7280', bgAlpha: '10', border: '20' },
    completed: { bg: '#00F0FF', bgAlpha: '10', border: '20' }
  };

  const colors = statusColors[campaign.status] || statusColors.pending;

  return (
    <div 
      className={`p-4 rounded-2xl bg-[#08080C] border border-white/10 ${onSelect ? 'cursor-pointer hover:bg-white/5' : ''} transition-all`}
      onClick={onSelect}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div 
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: `${colors.bg}${colors.bgAlpha}`, borderColor: `${colors.bg}${colors.border}` }}
          >
            <Target className="w-5 h-5" style={{ color: colors.bg }} />
          </div>
          <div>
            <p className="font-semibold text-white">{campaign.name}</p>
            <p className="text-sm text-gray-500">
              Budget: ${campaign.budget} • Daily: ${campaign.daily_budget}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <span 
            className="px-3 py-1 rounded-full text-xs font-medium capitalize border"
            style={{ 
              background: `${colors.bg}10`, 
              color: colors.bg,
              borderColor: `${colors.bg}20`
            }}
          >
            {campaign.status}
          </span>
          
          {!compact && (
            <div className="flex items-center gap-2">
              <div className="text-right">
                <p className="text-sm font-medium text-white">
                  {(campaign.impressions || 0).toLocaleString()} impressions
                </p>
                <p className="text-xs text-gray-500">
                  {(campaign.clicks || 0).toLocaleString()} clicks
                </p>
              </div>
              {onCreateAd && (
                <button
                  data-testid={`create-ad-${campaign.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCreateAd();
                  }}
                  className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                  title="Create Ad"
                >
                  <Plus className="w-4 h-4" />
                </button>
              )}
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Analytics Tab
const AnalyticsTab = ({ analytics, campaigns, onSelectCampaign }) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <h3 className="text-lg font-semibold text-white">Campaign Analytics</h3>
        <select
          data-testid="analytics-campaign-select"
          onChange={(e) => {
            const campaign = campaigns.find(c => c.id === e.target.value);
            if (campaign) onSelectCampaign(campaign);
          }}
          className="px-4 py-2 rounded-xl border bg-[#0F0F16] border-white/10 text-white outline-none focus:border-[#FFE800] transition-all"
        >
          <option value="" className="bg-[#0F0F16]">Select a campaign</option>
          {campaigns.map((campaign) => (
            <option key={campaign.id} value={campaign.id} className="bg-[#0F0F16]">{campaign.name}</option>
          ))}
        </select>
      </div>

      {analytics ? (
        <div className="space-y-4 sm:space-y-6">
          {/* Performance Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            {[
              { label: 'Impressions', value: analytics.totals?.impressions?.toLocaleString() || 0 },
              { label: 'Clicks', value: analytics.totals?.clicks?.toLocaleString() || 0 },
              { label: 'CTR', value: `${(analytics.totals?.ctr || 0).toFixed(2)}%` },
              { label: 'Spent', value: `$${(analytics.totals?.spent || 0).toFixed(2)}` }
            ].map((stat, index) => (
              <div key={index} className="p-3 sm:p-4 rounded-2xl bg-[#08080C] border border-white/10">
                <p className="text-xs sm:text-sm mb-1 text-gray-500">{stat.label}</p>
                <p className="text-lg sm:text-xl font-bold text-white">{stat.value}</p>
              </div>
            ))}
          </div>

          {/* Ads Performance */}
          {analytics.ads && analytics.ads.length > 0 && (
            <div>
              <h4 className="font-medium mb-3 text-white">Ad Creatives Performance</h4>
              <div className="space-y-3">
                {analytics.ads.map((ad) => (
                  <div key={ad.id} className="p-4 rounded-xl bg-[#08080C] border border-white/10">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-white">{ad.title}</p>
                        <p className="text-sm text-gray-500">{ad.ad_type}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-white">
                          {ad.impressions?.toLocaleString() || 0} imp • {ad.clicks?.toLocaleString() || 0} clicks
                        </p>
                        <p className="text-xs text-gray-500">
                          ${(ad.spend || 0).toFixed(2)} spent
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-12">
          <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-600" />
          <p className="text-gray-500">Select a campaign to view analytics</p>
        </div>
      )}
    </div>
  );
};

// Billing Tab
const BillingTab = ({ advertiser, onAddFunds }) => {
  const [amount, setAmount] = useState('');

  const fundOptions = [50, 100, 250, 500, 1000];

  return (
    <div className="space-y-6">
      <div className="p-6 rounded-2xl bg-gradient-to-br from-[#FFE800]/20 to-[#FF2A2A]/20 border border-[#FFE800]/20">
        <p className="text-gray-400 text-sm mb-2">Current Balance</p>
        <p className="text-4xl font-bold text-white mb-4">${(advertiser.balance || 0).toFixed(2)}</p>
        <p className="text-gray-500 text-sm">Total spent: ${(advertiser.total_spent || 0).toFixed(2)}</p>
      </div>

      <div>
        <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-white">Add Funds</h3>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-3 mb-4">
          {fundOptions.map((opt) => (
            <button
              key={opt}
              data-testid={`fund-option-${opt}`}
              onClick={() => setAmount(opt.toString())}
              className={`p-3 rounded-xl border font-medium transition-colors ${
                amount === opt.toString() 
                  ? 'bg-[#FFE800]/10 border-[#FFE800]/30 text-[#FFE800]' 
                  : 'bg-[#0F0F16] border-white/10 text-white hover:bg-white/5'
              }`}
            >
              ${opt}
            </button>
          ))}
        </div>

        <div className="flex gap-3">
          <input
            data-testid="custom-amount-input"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="Custom amount"
            className="flex-1 px-4 py-3 rounded-xl border bg-[#0F0F16] border-white/10 text-white placeholder-gray-600 outline-none focus:border-[#FFE800] focus:ring-2 focus:ring-[#FFE800]/20 transition-all"
          />
          <motion.button
            data-testid="add-funds-button"
            onClick={() => amount && onAddFunds(parseFloat(amount))}
            disabled={!amount || parseFloat(amount) <= 0}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="px-6 py-3 rounded-xl font-medium text-black bg-[#FFE800] shadow-lg shadow-[#FFE800]/20 disabled:opacity-50"
          >
            Add Funds
          </motion.button>
        </div>
      </div>
    </div>
  );
};

// Create Campaign Modal
const CreateCampaignModal = ({ onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    name: '',
    budget: '',
    daily_budget: '',
    start_date: '',
    end_date: '',
    targeting: {
      age_range: { min: 18, max: 65 },
      locations: [],
      languages: ['en'],
      interests: []
    }
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/advertiser/campaigns`, null, {
        params: {
          name: formData.name,
          budget: parseFloat(formData.budget),
          daily_budget: parseFloat(formData.daily_budget),
          start_date: formData.start_date,
          end_date: formData.end_date,
          targeting: JSON.stringify(formData.targeting)
        }
      });
      onCreated();
    } catch (error) {
      console.error('Error creating campaign:', error);
      alert(error.response?.data?.detail || 'Error creating campaign');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        className="bg-[#08080C] rounded-2xl shadow-2xl w-full max-w-lg p-6 border border-white/10"
      >
        <h3 className="text-xl font-bold mb-6 text-white">Create Campaign</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-300">Campaign Name</label>
            <input
              data-testid="campaign-name-input"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter campaign name"
              className="w-full px-4 py-3 rounded-xl border bg-[#0F0F16] border-white/10 text-white placeholder-gray-600 outline-none focus:border-[#FFE800] transition-all"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Total Budget ($)</label>
              <input
                data-testid="budget-input"
                type="number"
                value={formData.budget}
                onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                placeholder="1000"
                className="w-full px-4 py-3 rounded-xl border bg-[#0F0F16] border-white/10 text-white placeholder-gray-600 outline-none focus:border-[#FFE800] transition-all"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Daily Budget ($)</label>
              <input
                data-testid="daily-budget-input"
                type="number"
                value={formData.daily_budget}
                onChange={(e) => setFormData({ ...formData, daily_budget: e.target.value })}
                placeholder="50"
                className="w-full px-4 py-3 rounded-xl border bg-[#0F0F16] border-white/10 text-white placeholder-gray-600 outline-none focus:border-[#FFE800] transition-all"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Start Date</label>
              <input
                data-testid="start-date-input"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border bg-[#0F0F16] border-white/10 text-white outline-none focus:border-[#FFE800] transition-all"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">End Date</label>
              <input
                data-testid="end-date-input"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border bg-[#0F0F16] border-white/10 text-white outline-none focus:border-[#FFE800] transition-all"
                required
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 rounded-xl font-medium border border-white/10 text-gray-400 hover:bg-white/5 transition-colors"
            >
              Cancel
            </button>
            <motion.button
              data-testid="submit-campaign-button"
              type="submit"
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex-1 py-3 rounded-xl font-medium text-black bg-[#FFE800] disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Campaign'}
            </motion.button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Create Ad Modal
const CreateAdModal = ({ campaign, onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    ad_type: 'banner',
    title: '',
    description: '',
    image_url: '',
    cta_text: 'Learn More',
    cta_url: ''
  });
  const [loading, setLoading] = useState(false);

  const adTypes = [
    { id: 'banner', label: 'Banner Ad' },
    { id: 'native', label: 'Native Ad' },
    { id: 'video', label: 'Video Ad' },
    { id: 'sponsored_message', label: 'Sponsored Message' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/advertiser/campaigns/${campaign.id}/ads`, null, {
        params: formData
      });
      onCreated();
    } catch (error) {
      console.error('Error creating ad:', error);
      alert(error.response?.data?.detail || 'Error creating ad');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        className="bg-[#08080C] rounded-2xl shadow-2xl w-full max-w-lg p-6 border border-white/10"
      >
        <h3 className="text-xl font-bold mb-2 text-white">Create Ad</h3>
        <p className="text-sm mb-6 text-gray-500">For campaign: {campaign.name}</p>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-300">Ad Type</label>
            <div className="grid grid-cols-2 gap-2">
              {adTypes.map((type) => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => setFormData({ ...formData, ad_type: type.id })}
                  className={`p-3 rounded-xl border text-sm font-medium transition-colors ${
                    formData.ad_type === type.id
                      ? 'bg-[#FFE800]/10 border-[#FFE800]/30 text-[#FFE800]'
                      : 'bg-[#0F0F16] border-white/10 text-white hover:bg-white/5'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-gray-300">Title</label>
            <input
              data-testid="ad-title-input"
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Catchy ad title"
              className="w-full px-4 py-3 rounded-xl border bg-[#0F0F16] border-white/10 text-white placeholder-gray-600 outline-none focus:border-[#FFE800] transition-all"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-gray-300">Description</label>
            <textarea
              data-testid="ad-description-input"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe your offer..."
              className="w-full px-4 py-3 rounded-xl border bg-[#0F0F16] border-white/10 text-white placeholder-gray-600 outline-none focus:border-[#FFE800] resize-none transition-all"
              rows={3}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-gray-300">Image URL (optional)</label>
            <input
              data-testid="ad-image-input"
              type="url"
              value={formData.image_url}
              onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
              placeholder="https://..."
              className="w-full px-4 py-3 rounded-xl border bg-[#0F0F16] border-white/10 text-white placeholder-gray-600 outline-none focus:border-[#FFE800] transition-all"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">CTA Text</label>
              <input
                data-testid="ad-cta-text-input"
                type="text"
                value={formData.cta_text}
                onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                placeholder="Learn More"
                className="w-full px-4 py-3 rounded-xl border bg-[#0F0F16] border-white/10 text-white placeholder-gray-600 outline-none focus:border-[#FFE800] transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">CTA URL</label>
              <input
                data-testid="ad-cta-url-input"
                type="url"
                value={formData.cta_url}
                onChange={(e) => setFormData({ ...formData, cta_url: e.target.value })}
                placeholder="https://..."
                className="w-full px-4 py-3 rounded-xl border bg-[#0F0F16] border-white/10 text-white placeholder-gray-600 outline-none focus:border-[#FFE800] transition-all"
                required
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 rounded-xl font-medium border border-white/10 text-gray-400 hover:bg-white/5 transition-colors"
            >
              Cancel
            </button>
            <motion.button
              data-testid="submit-ad-button"
              type="submit"
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex-1 py-3 rounded-xl font-medium text-black bg-[#FFE800] disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Ad'}
            </motion.button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default AdvertiserDashboard;
